import type { Editor } from '@tiptap/core';
import * as React from 'react';
import { useDeepDiveMutation, useRefineTextMutation } from '../../../../queries/Support/useSupportThreadMutations';

export type ComposerAiAction = 'refine' | 'review' | 'draft' | 'selection';
type WholeDraftAiAction = Exclude<ComposerAiAction, 'selection'>;

const AI_PANEL_LABELS: Record<ComposerAiAction, string> = {
	refine: 'AI suggestion · Refine',
	review: 'AI suggestion · Review & rewrite',
	draft: 'AI suggestion · Draft reply',
	selection: 'AI suggestion · Refine selection',
};

interface WholeDraftAiSuggestion {
	type: 'whole-draft';
	action: WholeDraftAiAction;
	text: string;
}

interface SelectionAiSuggestion {
	type: 'selection';
	action: 'selection';
	text: string;
	selection: {
		from: number;
		to: number;
		text: string;
	};
}

type AiSuggestion = WholeDraftAiSuggestion | SelectionAiSuggestion;

type AiActionRequest =
	| {
			action: WholeDraftAiAction;
			request: () => Promise<string | null | undefined>;
	  }
	| {
			action: 'selection';
			selection: SelectionAiSuggestion['selection'];
			request: () => Promise<string | null | undefined>;
	  };

interface UseComposerAiOptions {
	ticketId: string;
	editor: Editor | null;
}

const getCurrentSelectionText = (editor: Editor, selection: SelectionAiSuggestion['selection']) => {
	const max = editor.state.doc.content.size;
	if (selection.from < 0 || selection.from > selection.to || selection.to > max) return null;
	return editor.state.doc.textBetween(selection.from, selection.to, '\n');
};

/**
 * Owns the AI-assist subsystem: the footer actions menu, the refine/review/draft/selection
 * requests, and the resulting suggestion panel. Accepting writes into the TipTap editor directly —
 * either replacing the whole draft or just the refined selection.
 */
export const useComposerAi = ({ ticketId, editor }: UseComposerAiOptions) => {
	const [showAiMenu, setShowAiMenu] = React.useState(false);
	const aiMenuRef = React.useRef<HTMLDivElement>(null);
	const [pendingAction, setPendingAction] = React.useState<ComposerAiAction | null>(null);
	const [suggestion, setSuggestion] = React.useState<AiSuggestion | null>(null);
	const [aiError, setAiError] = React.useState(false);

	const refineText = useRefineTextMutation(ticketId);
	const deepDive = useDeepDiveMutation(ticketId);

	React.useEffect(() => {
		if (!showAiMenu) return;
		const handleMouseDown = (event: MouseEvent) => {
			if (aiMenuRef.current && !aiMenuRef.current.contains(event.target as Node)) setShowAiMenu(false);
		};
		document.addEventListener('mousedown', handleMouseDown);
		return () => document.removeEventListener('mousedown', handleMouseDown);
	}, [showAiMenu]);

	const getEditorHtml = () => (editor && !editor.isEmpty ? editor.getHTML() : '');

	const runAiAction = async (aiRequest: AiActionRequest) => {
		setShowAiMenu(false);
		setAiError(false);
		setSuggestion(null);
		setPendingAction(aiRequest.action);
		try {
			const result = await aiRequest.request();
			if (result) {
				setSuggestion(
					aiRequest.action === 'selection'
						? { type: 'selection', action: 'selection', text: result, selection: aiRequest.selection }
						: { type: 'whole-draft', action: aiRequest.action, text: result }
				);
			}
		} catch {
			setAiError(true);
		} finally {
			setPendingAction(null);
		}
	};

	const runRefineSelection = () => {
		if (!editor) return;
		const { from, to, empty } = editor.state.selection;
		if (empty) return;
		const text = editor.state.doc.textBetween(from, to, '\n');
		if (!text.trim()) return;
		return runAiAction({
			action: 'selection',
			selection: { from, to, text },
			request: () => refineText.mutateAsync({ text, fullReview: false }),
		});
	};

	const acceptSuggestion = () => {
		if (!suggestion || !editor) return;
		if (suggestion.type === 'selection') {
			if (getCurrentSelectionText(editor, suggestion.selection) !== suggestion.selection.text) {
				setSuggestion(null);
				setAiError(true);
				return;
			}
			const { from, to } = suggestion.selection;
			editor.chain().focus().deleteRange({ from, to }).insertContent(suggestion.text).run();
		} else {
			editor.commands.setContent(suggestion.text);
			editor.commands.focus('end');
		}
		setSuggestion(null);
	};

	return {
		showAiMenu,
		setShowAiMenu,
		aiMenuRef,
		pendingAction,
		suggestion,
		panelLabel: AI_PANEL_LABELS[suggestion?.action ?? pendingAction ?? 'refine'],
		acceptLabel: suggestion?.type === 'selection' ? 'Replace selection' : 'Use this draft',
		acceptSuggestion,
		dismissSuggestion: () => setSuggestion(null),
		aiError,
		dismissAiError: () => setAiError(false),
		isBusy: refineText.isPending || deepDive.isPending,
		runRefine: () =>
			runAiAction({
				action: 'refine',
				request: () => refineText.mutateAsync({ text: getEditorHtml(), fullReview: false }),
			}),
		runReview: () =>
			runAiAction({
				action: 'review',
				request: () => refineText.mutateAsync({ text: getEditorHtml(), fullReview: true }),
			}),
		runDraft: () => runAiAction({ action: 'draft', request: () => deepDive.mutateAsync() }),
		runRefineSelection,
	};
};
